import { createContext, useContext, useState, useEffect } from "react";
import axios from 'axios';
import { nanoid } from "nanoid";
import { generateUserId } from '../utils/user';
import { extractAndValidateJson, getQuestionAnswer } from '../utils/message';
import { fetchConversations } from '../utils/api'; // Import the modified API functions

const ChatContext = createContext();

const SIZE = 10;
export const ChatProvider = ({ children }) => {
  const defaultColorSet = {
    // "--message-header-background-color": "#EB144C",
    // ... (add other colors as needed)
  };
  const [clientKey, setClientKey] = useState('');
  const [dataPrefix, setDataPrefix] = useState('');
  const [greeting, setGreeting] = useState('');
  const [header, setHeader] = useState('');

  const [messages, setMessages] = useState([]);

  const [theme, setTheme] = useState('#6ea9d7');
  const [colorSet, setColorSet] = useState(defaultColorSet);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadConversation, setLoadConversation] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [defaultIcon, setDefaultIcon] = useState("maira");

  const [userId, setUserId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isMobile, setIsMobile] = useState();
  const [pageUrl, setPageUrl] = useState('');
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [inputPlaceholder, setInputPlaceholder] = useState("");
  const [isNumberInput, setIsNumberInput] = useState(false);

  useEffect(() => {
    if (dataPrefix) {
      const storedUserId = localStorage.getItem(dataPrefix + '_userId');
      setUserId(storedUserId || generateUserId());
    }
  }, [dataPrefix]);

  useEffect(() => {
    if (dataPrefix) {
      localStorage.setItem(dataPrefix + '_userId', userId);
    }
  }, [userId, dataPrefix]);

  useEffect(() => {
    if (dataPrefix) {
      const storedSessionId = localStorage.getItem(dataPrefix + '_sessionId');
      if (storedSessionId) {
        setSessionId(storedSessionId);
      }
    }
  }, [dataPrefix]);

  useEffect(() => {
    if (dataPrefix) {
      localStorage.setItem(dataPrefix + '_sessionId', sessionId);
    }
  }, [sessionId, dataPrefix]);

  useEffect(() => {
    if (messages) {
      const question_data = messages[messages.length - 1]?.question_data;
      if (question_data?.type === "radio" || question_data?.type === "checkbox") {
        setIsInputDisabled(true);
      }
      if (question_data?.placeholder) {
        setInputPlaceholder(question_data.placeholder.trim())
      }
      if(question_data?.type === "number"){
        setIsNumberInput(true);
      }

    }
  }, [messages])

  const sendMessage = (message) => {
    setMessages(prevMessages => prevMessages.concat(message));
  }

  const setCustomTheme = (theme) => {
    setTheme(theme);
  }

  const setCustomColorSet = (colorSet) => {
    setColorSet(colorSet);
  }

  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const showErrorMessageOnNumberValidation = () => {
    let avatarIcon = defaultIcon;
    let currentMessages = [];
    currentMessages.push({
      _id: nanoid(),
      text: "Please enter a valid number",
      type: 'incoming',
      user: 'remote',
      avatar: avatarIcon,
      createdAt: new Date(),
    });
    setMessages(prevMessages => prevMessages.concat(currentMessages));
    incomingMessage(currentMessages.length);
  }

  const makeApiCall = async (message, questionnaire_init = false) => {
    if (error) {
      setError('');
    }
    const requestData = {
      clientKey,
      message: message,
      user_id: userId,
      member_id: memberId,
      session_id: sessionId,
      questionnaire_init: questionnaire_init
    };
    if (questionnaire_init) {
      requestData["questionnaire_init"] = true;
    }
    try {
      const response = await axios.post(process.env.REACT_APP_MAIRA_AI_ASSISTANT_URL + 'api/ask', requestData);
      const response_session_id = response.data.detail.session_id;
      const is_questionnaire_complete = response.data.detail.questionnaire_completed;

      if (response_session_id) setSessionId(response_session_id);
      const response_msg = response.data.detail.response;
      const question_data = response.data.detail.question_data;
      if (questionnaire_init && !question_data) {
        if (messages.length === 0 && greeting !== '') {
          sendMessage({
            _id: nanoid(),
            text: greeting,
            avatar: defaultIcon,
            user: "remote",
            type: "incoming",
            createdAt: new Date(),
          });
        }
        return;
      }

      setStartIndex(prevState => prevState + 1);
      const { parsedJson, nonJsonPart } = extractAndValidateJson(response_msg);

      let currentMessages = [];

      let avatarIcon = defaultIcon;

      if (nonJsonPart && nonJsonPart !== "") {
        if (parsedJson && !parsedJson.comment && !parsedJson.recommendations) {
          currentMessages.push({
            _id: nanoid(),
            text: response_msg,
            type: 'incoming',
            user: 'remote',
            question_data: question_data,
            avatar: avatarIcon,
            createdAt: new Date(),
          });
        } else {
          currentMessages.push({
            _id: nanoid(),
            text: nonJsonPart,
            type: 'incoming',
            user: 'remote',
            question_data: question_data,
            avatar: avatarIcon,
            createdAt: new Date(),
          });
        }
      }
      if (parsedJson && parsedJson !== null && Object.keys(parsedJson).length === 0 && !currentMessages.length) {
        currentMessages.push({
          _id: nanoid(),
          text: "現在、アクセスが集中しています。しばらくしてから再度お試しください。",
          type: 'incoming',
          user: 'remote',
          avatar: avatarIcon,
          createdAt: new Date(),
        });
      }
      else if (parsedJson && parsedJson !== null) {
        if (parsedJson.comment) {
          currentMessages.push({
            _id: nanoid(),
            text: parsedJson.comment,
            type: 'incoming',
            user: 'remote',
            avatar: avatarIcon,
            createdAt: new Date(),
          });
        }
        if (parsedJson.recommendations) {
          currentMessages.push({
            _id: nanoid(),
            text: parsedJson.comment,
            recommendations: parsedJson.recommendations,
            type: 'carousel',
            user: 'remote',
            createdAt: new Date(),
          });
        }
      }

      setMessages(prevMessages => prevMessages.concat(currentMessages));
      incomingMessage(currentMessages.length); //Signal to frontend new message is received
      if (is_questionnaire_complete) {
        makeApiCall(message = "", true);
      }
    } catch (error) {
      // Handle errors (e.g., update state or log)
      //console.error('API Error:', error);
      setMessages(prevMessages => prevMessages.concat({
        _id: nanoid(),
        text: '現在、アクセスが集中しています。しばらくしてから再度お試しください。',
        type: 'incoming',
        user: 'remote',
        avatar: defaultIcon,
        createdAt: new Date(),
      }));

      incomingMessage(1); //Signal to frontend new message is received
    }
  };

  const fetchConversations = async () => {
    try {
      const requestData = {
        clientKey,
        user_id: userId,
        member_id: memberId,
        start: startIndex,
        size: SIZE
      };
      const response = await axios.post(
        process.env.REACT_APP_MAIRA_AI_ASSISTANT_URL + 'api/conversations',
        requestData
      );
      const conversations = response.data;
      if (!conversations.length) {
        if (messages.length === 0) makeApiCall("", true).then().catch();
        return;
      }
      let next_conversation_index = 1;
      const formattedMessages = conversations.reverse().flatMap(conversation => {
        let currentMessages = [];
        if (conversation.request_body.query) {
          const userMsg = {
            _id: nanoid(),
            text: conversation.request_body.query,
            type: 'outgoing',
            user: 'local',
            createdAt: new Date(conversation.created_at),
          };
          currentMessages.push(userMsg);
        }
        try {
          const response = conversation.response || conversation.response_msg;
          const question_data = conversation.question_data;
          const { parsedJson, nonJsonPart } = extractAndValidateJson(response);

          let avatarIcon = defaultIcon;

          if (nonJsonPart && nonJsonPart !== "") {
            if (parsedJson && !parsedJson.comment && !parsedJson.recommendations) {
              currentMessages.push({
                _id: nanoid(),
                text: response,
                type: 'incoming',
                user: 'remote',
                question_data: question_data,
                question_data_answer: question_data ? getQuestionAnswer(question_data, next_conversation_index < conversations.length ? conversations[next_conversation_index] : []) : "",
                avatar: avatarIcon,
                createdAt: new Date(conversation.created_at),
              });
              next_conversation_index += 1;
            } else {
              currentMessages.push({
                _id: nanoid(),
                text: nonJsonPart,
                type: 'incoming',
                user: 'remote',
                avatar: avatarIcon,
                question_data: question_data,
                question_data_answer: question_data ? getQuestionAnswer(question_data, next_conversation_index < conversations.length ? conversations[next_conversation_index] : []) : "",
                createdAt: new Date(conversation.created_at),
              });
              next_conversation_index += 1;
            }
          }

          if (parsedJson && parsedJson !== null && Object.keys(parsedJson).length === 0 && !currentMessages.length) {
            currentMessages.push({
              _id: nanoid(),
              text: "現在、アクセスが集中しています。しばらくしてから再度お試しください。",
              type: 'incoming',
              user: 'remote',
              avatar: avatarIcon,
              createdAt: new Date(conversation.created_at),
            });
          }
          else if (parsedJson && parsedJson !== null) {
            if (parsedJson.comment) {
              currentMessages.push({
                _id: nanoid(),
                text: parsedJson.comment,
                type: 'incoming',
                user: 'remote',
                avatar: avatarIcon,
                createdAt: new Date(conversation.created_at),
              });
            }
            if (parsedJson.recommendations) {
              currentMessages.push({
                _id: nanoid(),
                text: parsedJson.comment,
                recommendations: parsedJson.recommendations,
                type: 'carousel',
                user: 'remote',
                avatar: avatarIcon,
                createdAt: new Date(conversation.created_at),
              });
            }
          }
          return currentMessages;
        } catch (error) {
          currentMessages.push({
            _id: nanoid(),
            text: '現在、アクセスが集中しています。しばらくしてから再度お試しください。',
            type: 'incoming',
            user: 'remote',
            avatar: defaultIcon,
            createdAt: new Date(conversation.created_at),
          });
          return currentMessages;
        }

      });

      setStartIndex(prevState => prevState + SIZE);
      // Update messages state
      setMessages(prevMessages => formattedMessages.concat(prevMessages));
    } catch (error) {
      // Handle errors
      // console.log(error);
      setError(error.response.data.error);
      setLoading(false);
    }
  };
  const hide = () => {
    window.parent.postMessage("hide", "*");
  }
  const show = () => {
    window.parent.postMessage("show", "*");
  }
  const enterFullScreen = () => {
    window.parent.postMessage("enterFullScreen", "*");
  }
  const exitFullScreen = () => {
    window.parent.postMessage("exitFullScreen", "*");
  }

  const incomingMessage = (count) => {
    window.parent.postMessage({ type: "incomingMessage", value: count }, "*");
  }

  const trackLinkClick = async ({ url, text }) => {
    try {
      const deviceType = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
      await axios.post(process.env.REACT_APP_MAIRA_AI_ASSISTANT_URL + 'api/interactions', {
        clientKey,
        user_id: userId,
        member_id: memberId || undefined,
        interaction_point: 'widget',
        page_url: pageUrl || document.referrer || window.location.href,
        text_in_interaction_point: text,
        interaction_value: url,
        device_type: deviceType,
      });
    } catch (_) {}
  };

  return <ChatContext.Provider value={{
    clientKey,
    setClientKey,
    isMobile,
    setIsMobile,
    messages,
    sendMessage,
    hide,
    show,
    enterFullScreen,
    exitFullScreen,
    colorSet,
    setCustomColorSet,
    theme,
    setCustomTheme,
    makeApiCall,
    setShowTypingIndicator,
    showTypingIndicator,
    fetchConversations,
    loading,
    setLoading,
    setLoadConversation,
    loadConversation,
    setGreeting,
    setDataPrefix,
    setHeader,
    header,
    startIndex,
    error,
    setError,
    setUserId,
    setMemberId,
    setPageUrl,
    incomingMessage,
    isInputDisabled,
    inputPlaceholder,
    isNumberInput,
    setIsNumberInput,
    setIsInputDisabled,
    setInputPlaceholder,
    setStartIndex,
    setMessages,
    showErrorMessageOnNumberValidation,
    trackLinkClick,
  }}>{children}</ChatContext.Provider>

}

export const useChat = () => {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChatContext must be within ChatProvider");
  }

  return context;
}